import { create } from 'zustand';
import { api } from './api';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: 'ADMIN' | 'PROVIDER' | 'STAFF';
  active: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  expiresAt: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  acceptSsoToken: (token: string, expiresAt?: string) => Promise<void>;
  logout: () => void;
  hydrate: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('census.accessToken'),
  expiresAt: localStorage.getItem('census.expiresAt'),
  loading: true,
  async login(email, password) {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('census.accessToken', data.accessToken);
    localStorage.setItem('census.expiresAt', data.expiresAt ?? '');
    set({ user: data.user, token: data.accessToken, expiresAt: data.expiresAt ?? null, loading: false });
  },
  async acceptSsoToken(token, expiresAt) {
    // Token already minted by /auth/oidc/callback. Persist it, then fetch /auth/me
    // so the user object hydrates the same shape as a password login.
    localStorage.setItem('census.accessToken', token);
    localStorage.setItem('census.expiresAt', expiresAt ?? '');
    set({ token, expiresAt: expiresAt ?? null, loading: true });
    const { data } = await api.get('/auth/me');
    set({ user: data, token, expiresAt: expiresAt ?? null, loading: false });
  },
  logout() {
    localStorage.removeItem('census.accessToken');
    localStorage.removeItem('census.expiresAt');
    set({ user: null, token: null, expiresAt: null });
  },
  async hydrate() {
    const token = localStorage.getItem('census.accessToken');
    if (!token) {
      set({ user: null, token: null, expiresAt: null, loading: false });
      return;
    }
    // Proactively log out if the stored token is already expired, avoiding
    // a stale "logged in" state that would fail on the first API call anyway.
    const storedExpiresAt = localStorage.getItem('census.expiresAt');
    if (storedExpiresAt && new Date(storedExpiresAt).getTime() < Date.now()) {
      localStorage.removeItem('census.accessToken');
      localStorage.removeItem('census.expiresAt');
      set({ user: null, token: null, expiresAt: null, loading: false });
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data, token, expiresAt: storedExpiresAt, loading: false });
    } catch {
      localStorage.removeItem('census.accessToken');
      localStorage.removeItem('census.expiresAt');
      set({ user: null, token: null, expiresAt: null, loading: false });
    }
  },
}));
